/**
 * Custom Capacitor Plugin: HealthKitWater
 * 
 * Queries HKQuantityTypeIdentifier.dietaryWater from Apple HealthKit
 * and HydrationRecord from Android Health Connect.
 * 
 * This plugin exists because @capgo/capacitor-health does NOT support
 * dietaryWater, which is the data type LARQ bottles write to HealthKit.
 * 
 * Native implementation must be added manually to the Xcode/Android Studio projects.
 */

import { registerPlugin } from '@capacitor/core';

export interface WaterSample {
  /** Amount in milliliters */
  valueMl: number;
  /** ISO 8601 timestamp */
  startDate: string;
  /** ISO 8601 timestamp */
  endDate: string;
  /** Source app/device name (e.g. "LARQ", "Wellebit") */
  sourceName: string;
  /** Bundle ID of the source app */
  sourceBundleId?: string;
}

export interface ReadWaterIntakeOptions {
  /** ISO 8601 start date */
  startDate: string;
  /** ISO 8601 end date */
  endDate: string;
}

export interface ReadWaterIntakeResult {
  samples: WaterSample[];
  totalMl: number;
}

export interface NutritionSummaryResult {
  caloriesConsumed: number;
  caffeine: number;
  sodium: number;
  protein: number;
  carbs: number;
  fat: number;
}

export interface HealthKitWaterPlugin {
  /**
   * Request read permission for dietaryWater from HealthKit / Health Connect.
   */
  requestAuthorization(): Promise<{ granted: boolean }>;

  /**
   * Read water intake samples within a date range.
   */
  readWaterIntake(options: ReadWaterIntakeOptions): Promise<ReadWaterIntakeResult>;

  /**
   * Read today's nutrition summary (dietary energy, caffeine, sodium, protein, carbs, fat)
   * from HealthKit. These types are not supported by @capgo/capacitor-health.
   */
  readNutritionSummary(options: { startDate: string; endDate: string }): Promise<NutritionSummaryResult>;
}

const HealthKitWater = registerPlugin<HealthKitWaterPlugin>('HealthKitWater');

export default HealthKitWater;
